// src/hooks/useTemplates.js
// Hook for fetching and managing WhatsApp templates from Firestore

import { useState, useEffect, useCallback, useMemo } from 'react';
import TemplateAPI from '../services/api/templateAPI';

const TEMPLATES_STORAGE_KEY = 'salon_templates_cache';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Hook for managing WhatsApp templates
 * Fetches templates from Firestore via the backend API
 */
export function useTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Cache helpers
  const saveToCache = useCallback((data) => {
    try {
      localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify({
        templates: data,
        timestamp: Date.now()
      }));
    } catch (err) {
      console.warn('Failed to cache templates:', err);
    }
  }, []);

  const loadFromCache = useCallback(() => {
    try {
      const cached = localStorage.getItem(TEMPLATES_STORAGE_KEY);
      if (!cached) return null;

      const { templates, timestamp } = JSON.parse(cached);
      const isExpired = Date.now() - timestamp > CACHE_TTL_MS;

      if (isExpired) {
        localStorage.removeItem(TEMPLATES_STORAGE_KEY);
        return null;
      }

      return templates;
    } catch (err) {
      console.warn('Failed to load templates from cache:', err);
      return null;
    }
  }, []);

  // Load templates
  const loadTemplates = useCallback(async (forceReload = false) => {
    if (loading) return;

    // Check cache first
    if (!forceReload) {
      const cached = loadFromCache();
      if (cached && cached.length > 0) {
        console.log('useTemplates: Using cached templates', { count: cached.length });
        setTemplates(cached);
        setHasLoaded(true);
        return cached;
      }
    }

    setLoading(true);
    setError(null);

    try {
      console.log('useTemplates: Fetching templates from API');
      const data = await TemplateAPI.getAll(true); // activeOnly=true

      setTemplates(data);
      saveToCache(data);
      setHasLoaded(true);

      console.log('useTemplates: Templates loaded', { count: data.length });
      return data;
    } catch (err) {
      console.error('useTemplates: Error loading templates:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loading, loadFromCache, saveToCache]);

  // Load on mount
  useEffect(() => {
    if (!hasLoaded) {
      loadTemplates(false);
    }
  }, [hasLoaded, loadTemplates]);

  // Get template by ID
  const getTemplate = useCallback((templateId) => {
    return templates.find(t => t.id === templateId) || null;
  }, [templates]);

  // Get templates by trigger type
  const getTemplatesByTrigger = useCallback((triggerType) => {
    return templates.filter(t =>
      t.triggerTypes && t.triggerTypes.includes(triggerType)
    );
  }, [templates]);

  // Get default template for trigger type
  const getDefaultTemplate = useCallback((triggerType) => {
    const matching = getTemplatesByTrigger(triggerType);
    return matching.length > 0 ? matching[0] : null;
  }, [getTemplatesByTrigger]);

  // Generate preview with sample data
  const generatePreview = useCallback((templateId, sampleData = null) => {
    const template = getTemplate(templateId);
    if (!template || !template.content) {
      return 'Template not found';
    }

    const defaultSampleData = {
      customer_name: 'שרה כהן',
      service_name: 'טיפול פנים',
      business_name: 'סיון מנדל אסתטיקה ויופי',
      date: 'יום שני, 15 בינואר',
      time: '14:30',
      business_location: 'פתח תקווה, רחוב הרצל 123',
      business_phone: '050-6376699',
      business_slug: 'sivan-mandel',
      code: '1234'
    };

    const data = sampleData || defaultSampleData;
    let preview = template.content;

    // Replace variables based on order
    if (template.variables) {
      template.variables.forEach((variable) => {
        const placeholder = `{{${variable.order}}}`;
        const value = data[variable.name] || `[${variable.displayName}]`;
        preview = preview.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), value);
      });
    }

    return preview;
  }, [getTemplate]);

  // Clear cache and reload
  const refresh = useCallback(async () => {
    localStorage.removeItem(TEMPLATES_STORAGE_KEY);
    setHasLoaded(false);
    return await loadTemplates(true);
  }, [loadTemplates]);

  // Computed values
  const automationTemplates = useMemo(() => {
    return templates.filter(t => !t.isCampaignTemplate);
  }, [templates]);

  const campaignTemplates = useMemo(() => {
    return templates.filter(t => t.isCampaignTemplate);
  }, [templates]);

  return {
    // State
    templates,
    automationTemplates,
    campaignTemplates,
    loading,
    error,
    hasLoaded,

    // Methods
    loadTemplates,
    refresh,
    getTemplate,
    getTemplatesByTrigger,
    getDefaultTemplate,
    generatePreview
  };
}
